import { hasRoute } from '@common/utils'
import { useSessionContext } from '@contexts/session'
import useIsModLogin from '@hooks/useIsModLogin'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { useEffect, useState } from 'react'

declare global {
  interface Window {
    Atlas: any
  }
}

const AtlasWidget = () => {
  const {
    id,
    teacher,
    isPaidUser,
    isCompetitionUser,
    loggedIn,
    email,
    name,
    gender,
    hasLaptop,
    phone,
    student,
    timezone,
    country,
    totalCredits,
    leadSource,
    dob,
    totalCodingCredits,
    totalMathCredits,
    totalReferralCredits,
    signupSource,
    preferences,
  } = useSessionContext()
  const { isModLogin } = useIsModLogin()
  const [userDetails, setUserDetails] = useState({})
  const { pathname } = useRouter()
  const isTeacher = teacher?.id ? true : false

  const RESTRICTED_PATHS = [
    '/feedback/**',
    '/quizzes/**/arena**',
    '/competitions/**/register/complete/**',
    '/payment-partners*',
    '/payment-status*',
    '/payment/**',
    '/batches/**',
    '/embed/**',
    '/apps/scratch',
    '/apps/scratch/**',
    '/teacher/application*',
    '/teacher/application/**',
    '/teacher/overview*',
    '/teacher/overview/**',
    '/trial-class',
    '/trial-class/**',
  ]
  // Trial-class pages where the Atlas widget should remain enabled,
  // overriding the broad '/trial-class/**' restriction above.
  const ALLOWED_PATHS = [
    '/trial-class/complete',
    '/trial-class/complete/**',
    '/trial-class/incomplete',
    '/trial-class/join',
  ]
  const isProd = process.env.NODE_ENV === 'production'

  // Update userDetails whenever session context changes
  useEffect(() => {
    if (!loggedIn) return // No need to set userDetails if not logged in

    const updatedUserDetails = {
      userId: id,
      email: email,
      name: name,
      phoneNumber: phone,
      customFields: {
        teacherId: teacher ? `t${teacher?.id}` : '',
        studentId: student ? `s${student?.id}` : '',
        country: country,
        totalCredits: totalCredits,
        studentGrade: student?.grade,
        gender: gender,
        isTeacher: isTeacher,
        isPaidUser: isPaidUser,
        isCompetitionUser: isCompetitionUser,
        hasLaptop: hasLaptop,
        timezone: timezone,
        relationshipManagerName: student?.relationshipManager?.name ?? '',
        relationshipManagerPhone: student?.relationshipManager?.phone ?? '',
        relationshipManagerEmail: student?.relationshipManager?.email ?? '',
        codingClassTeacherName: student?.codingClassTeacherDetails?.name ?? '',
        codingClassTeacherEmail:
          student?.codingClassTeacherDetails?.email ?? '',
        mathClassTeacherName: student?.mathClassTeacherDetails?.name ?? '',
        mathClassTeacherEmail: student?.mathClassTeacherDetails?.email ?? '',
        elaClassTeacherName: student?.elaClassTeacherDetails?.name ?? '',
        elaClassTeacherEmail: student?.elaClassTeacherDetails?.email ?? '',
        whatsappGroupUrl: student?.whatsappGroupUrl ?? '',
        dateOfBirth: dob ?? '',
        totalCodingCredits: totalCodingCredits ?? '',
        totalMathCredits: totalMathCredits ?? '',
        totalReferralCredits: totalReferralCredits ?? '',
        utmSource: leadSource?.utmSource ?? '',
        utmMedium: leadSource?.utmMedium ?? '',
        signupSource: signupSource ?? '',
        parentName: student?.parentName ?? '',
        enrolled_subject: student?.enrolledSubjects ?? [],
        whatsappEnabled: preferences?.whatsappEnabled ?? true,
        isTrialClassScheduled: student?.isTrialClassScheduled ?? false,
        autoLoginUrl: student?.autoLoginUrl ?? '',
        vipStatus: student?.isVip ?? false,
      },
    }

    setUserDetails(updatedUserDetails)
  }, [
    id,
    teacher,
    isPaidUser,
    isCompetitionUser,
    email,
    name,
    gender,
    hasLaptop,
    phone,
    student,
    timezone,
    country,
    totalCredits,
    loggedIn,
  ])

  useEffect(() => {
    const isAllowedPath = hasRoute(pathname, ALLOWED_PATHS)
    const isRestrictedPath =
      !isAllowedPath && hasRoute(pathname, RESTRICTED_PATHS)

    // Hide chat widget on restricted paths
    if (isRestrictedPath) {
      window.Atlas?.call('update', {
        chat: {
          hideBubble: true,
        },
      })
      return
    }
    // Show chat widget on all other paths
    window.Atlas?.call('update', {
      chat: {
        showBubble: true,
        hideBubble: false,
        position: 'bottomRight',
        offset: [20, 20],
      },
    })
    // Show chat widget on left side on competition arena, and disable label
    if (hasRoute(pathname, ['/competitions/**/problems/**'])) {
      window.Atlas?.call('update', {
        chat: {
          position: 'bottomLeft',
          offset: [20, 20],
          launcher: {
            label: '',
          },
        },
      })
    }

    if (!loggedIn || Object.keys(userDetails).length === 0 || !window.Atlas) {
      return
    }

    window.Atlas?.call('identify', userDetails)
  }, [userDetails, loggedIn, pathname])

  // Staff viewing a user's dashboard must not chat or rate as that user
  if (!isProd || isModLogin) return null

  return (
    <Script
      id="atlas-snippet"
      dangerouslySetInnerHTML={{
        __html: `${
          isTeacher
            ? `(()=>{"use strict";var t,e={appId:"phg0lup4ll",v:2,q:[],call:function(){this.q.push(arguments)}};window.Atlas=e;var n=document.createElement("script");n.async=!0,n.src="https://app.atlas.so/client-js/atlas.bundle.js";var s=document.getElementsByTagName("script")[0];null===(t=s.parentNode)||void 0===t||t.insertBefore(n,s)})();window.Atlas.call("start",{chat:{withChatbot: ["t_s_d_chatbot", { prefer: "last" }],openIncoming: false}});`
            : `(()=>{"use strict";var t,e={appId:"phg0lup4ll",v:2,q:[],call:function()
  {this.q.push(arguments)}};window.Atlas=e;var
  n=document.createElement("script");n.async=!0,n.src="https://app.atlas.so/client-js/atlas.bundle.js";
  var s=document.getElementsByTagName("script")[0];null===(t=s.parentNode)||void 0===t||t.insertBefore(n,s)})();
  window.Atlas.call("start");`
        }`,
      }}
    />
  )
}
export default AtlasWidget
